import { lazy, Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import { AppRoutes } from "./AppRoutes";
import { PrivateRoute } from "../helpers/PrivateRoute";
import { TeacherOrAdminRoute } from "../helpers/TeacherOrAdminRoute";
import { LoginPage } from "../pages/login/LoginPage";
import { DocAppPage } from "../components/DocAppPage";
import { isAuthenticated } from "../model/Functions";

// Code-splitting: les pages authentifiées sont chargées à la demande (bundle initial allégé).
const DocCatalogPage = lazy(() =>
  import("../pages/catalog/DocCatalogPage").then((m) => ({ default: m.DocCatalogPage })),
);
const SearchResultsPage = lazy(() =>
  import("../pages/search/SearchResultsPage").then((m) => ({ default: m.SearchResultsPage })),
);
const DocViewPage = lazy(() =>
  import("../pages/viewer/DocViewPage").then((m) => ({ default: m.DocViewPage })),
);
const NewDocPage = lazy(() =>
  import("../pages/new/NewDocPage").then((m) => ({ default: m.NewDocPage })),
);
const EditDocPage = lazy(() =>
  import("../pages/edit/EditDocPage").then((m) => ({ default: m.EditDocPage })),
);

function RouteFallback() {
  return (
    <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
      <CircularProgress />
    </Box>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <DocAppPage>
      <Suspense fallback={<RouteFallback />}>{children}</Suspense>
    </DocAppPage>
  );
}
export function AppRouter() {
  return (
    <BrowserRouter basename={AppRoutes.BASENAME}>
      <Routes>
        <Route path={AppRoutes.ROOT} element={<LoginPage />} />
        {/* Accueil retiré : le catalogue est la page par défaut. On garde une
            redirection pour ne pas casser les liens /home existants. */}
        <Route path={AppRoutes.HOME} element={<Navigate to={AppRoutes.CATALOG} replace />} />
        <Route
          path={AppRoutes.CATALOG}
          element={
            <PrivateRoute>
              <Shell>
                <DocCatalogPage />
              </Shell>
            </PrivateRoute>
          }
        />
        <Route
          path={AppRoutes.SEARCH}
          element={
            <PrivateRoute>
              <Shell>
                <SearchResultsPage />
              </Shell>
            </PrivateRoute>
          }
        />
        <Route
          path="/docs/new"
          element={
            <PrivateRoute>
              <TeacherOrAdminRoute>
                <Shell>
                  <NewDocPage />
                </Shell>
              </TeacherOrAdminRoute>
            </PrivateRoute>
          }
        />
        <Route
          path={AppRoutes.EDIT}
          element={
            <PrivateRoute>
              <TeacherOrAdminRoute>
                <Shell>
                  <EditDocPage />
                </Shell>
              </TeacherOrAdminRoute>
            </PrivateRoute>
          }
        />
        <Route
          path="/docs/:id"
          element={
            <PrivateRoute>
              <Shell>
                <DocViewPage />
              </Shell>
            </PrivateRoute>
          }
        />
        <Route
          path="*"
          element={<Navigate to={isAuthenticated() ? AppRoutes.CATALOG : AppRoutes.ROOT} replace />}
        />
      </Routes>
    </BrowserRouter>
  );
}
